/**
 * Theme Context - User-adjustable color scheme and dark/light mode
 * Allows users to customize the app's accent color and appearance mode
 */
import { createContext, useContext, useState, useEffect } from 'react';

// Available color themes
export const COLOR_THEMES = {
  amber: {
    name: 'Amber Gold',
    primary: '43 96% 50%',
    primaryHex: '#f59e0b',
    ring: '43 96% 50%',
    description: 'Default warm golden tone'
  },
  teal: {
    name: 'Teal Ocean',
    primary: '168 76% 42%',
    primaryHex: '#14b8a6',
    ring: '168 76% 42%',
    description: 'Cool professional teal'
  },
  blue: {
    name: 'Ocean Blue',
    primary: '217 91% 60%',
    primaryHex: '#3b82f6',
    ring: '217 91% 60%',
    description: 'Classic trusted blue'
  },
  purple: {
    name: 'Royal Purple',
    primary: '271 91% 65%',
    primaryHex: '#a855f7',
    ring: '271 91% 65%',
    description: 'Premium purple accent'
  },
  emerald: {
    name: 'Emerald Green',
    primary: '160 84% 39%',
    primaryHex: '#10b981',
    ring: '160 84% 39%',
    description: 'Fresh natural green'
  },
  rose: {
    name: 'Rose Pink',
    primary: '350 89% 60%',
    primaryHex: '#f43f5e',
    ring: '350 89% 60%',
    description: 'Vibrant rose accent'
  },
  orange: {
    name: 'Sunset Orange',
    primary: '25 95% 53%',
    primaryHex: '#f97316',
    ring: '25 95% 53%',
    description: 'Energetic orange'
  },
  cyan: {
    name: 'Cyan Bright',
    primary: '189 94% 43%',
    primaryHex: '#06b6d4',
    ring: '189 94% 43%',
    description: 'Fresh cyan accent'
  }
};

// Appearance modes
export const APPEARANCE_MODES = {
  dark: {
    name: 'Dark Mode',
    description: 'Easy on the eyes',
    icon: 'moon',
    background: '222.2 84% 4.9%',
    foreground: '210 40% 98%',
    card: '222.2 84% 4.9%',
    cardForeground: '210 40% 98%',
    muted: '217.2 32.6% 17.5%',
    mutedForeground: '215 20.2% 65.1%',
    border: '217.2 32.6% 17.5%',
    input: '217.2 32.6% 17.5%',
  },
  light: {
    name: 'Light Mode',
    description: 'Classic bright look',
    icon: 'sun',
    background: '0 0% 100%',
    foreground: '222.2 84% 4.9%',
    card: '0 0% 100%',
    cardForeground: '222.2 84% 4.9%',
    muted: '210 40% 96.1%',
    mutedForeground: '215.4 16.3% 46.9%',
    border: '214.3 31.8% 91.4%',
    input: '214.3 31.8% 91.4%',
  },
  system: {
    name: 'System',
    description: 'Match device settings',
    icon: 'monitor',
  }
};

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [colorTheme, setColorTheme] = useState('amber');
  const [appearanceMode, setAppearanceMode] = useState('dark');
  const [resolvedMode, setResolvedMode] = useState('dark');
  
  // Load saved preferences from localStorage on mount
  useEffect(() => {
    const savedColorTheme = localStorage.getItem('liantiao_color_theme');
    const savedAppearance = localStorage.getItem('liantiao_appearance_mode');
    
    if (savedColorTheme && COLOR_THEMES[savedColorTheme]) {
      setColorTheme(savedColorTheme);
    }
    
    if (savedAppearance && APPEARANCE_MODES[savedAppearance]) {
      setAppearanceMode(savedAppearance);
    }
    
    // Apply initial theme
    applyColorTheme(savedColorTheme || 'amber');
    applyAppearanceMode(savedAppearance || 'dark');
  }, []);
  
  // Listen for system preference changes
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    const handleChange = (e) => {
      if (appearanceMode === 'system') {
        const newMode = e.matches ? 'dark' : 'light';
        setResolvedMode(newMode);
        applyAppearanceModeStyles(newMode);
      }
    };
    
    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, [appearanceMode]);
  
  // Apply CSS variables for the selected color theme
  const applyColorTheme = (themeName) => {
    const theme = COLOR_THEMES[themeName];
    if (!theme) return;
    
    const root = document.documentElement;
    root.style.setProperty('--primary', theme.primary);
    root.style.setProperty('--ring', theme.ring);
    root.style.setProperty('--selection-bg', theme.primaryHex + '4D');
  };
  
  // Apply appearance mode styles
  const applyAppearanceModeStyles = (mode) => {
    const modeConfig = APPEARANCE_MODES[mode];
    if (!modeConfig || mode === 'system') return;
    
    const root = document.documentElement;
    
    // Toggle class for Tailwind dark mode
    if (mode === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    
    // Apply CSS variables
    root.style.setProperty('--background', modeConfig.background);
    root.style.setProperty('--foreground', modeConfig.foreground);
    root.style.setProperty('--card', modeConfig.card);
    root.style.setProperty('--card-foreground', modeConfig.cardForeground);
    root.style.setProperty('--muted', modeConfig.muted);
    root.style.setProperty('--muted-foreground', modeConfig.mutedForeground);
    root.style.setProperty('--border', modeConfig.border);
    root.style.setProperty('--input', modeConfig.input);
  };
  
  // Apply appearance mode (handles system preference)
  const applyAppearanceMode = (mode) => {
    if (mode === 'system') {
      const systemPrefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      const resolvedSystemMode = systemPrefersDark ? 'dark' : 'light';
      setResolvedMode(resolvedSystemMode);
      applyAppearanceModeStyles(resolvedSystemMode);
    } else {
      setResolvedMode(mode);
      applyAppearanceModeStyles(mode);
    }
  };
  
  const changeColorTheme = (themeName) => {
    if (!COLOR_THEMES[themeName]) return;
    
    setColorTheme(themeName);
    localStorage.setItem('liantiao_color_theme', themeName);
    applyColorTheme(themeName);
  };
  
  const changeAppearanceMode = (mode) => {
    if (!APPEARANCE_MODES[mode]) return;
    
    setAppearanceMode(mode);
    localStorage.setItem('liantiao_appearance_mode', mode);
    applyAppearanceMode(mode);
  };
  
  const getCurrentTheme = () => COLOR_THEMES[colorTheme];
  const isDarkMode = () => resolvedMode === 'dark';
  
  return (
    <ThemeContext.Provider value={{
      colorTheme,
      changeColorTheme,
      getCurrentTheme,
      availableThemes: COLOR_THEMES,
      appearanceMode,
      resolvedMode,
      changeAppearanceMode,
      availableModes: APPEARANCE_MODES,
      isDarkMode
    }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;
